import React, { useState, useEffect, useRef } from 'react';
import {
  FiUser, FiLogOut, FiMenu, FiChevronDown, FiCheckSquare,
  FiSettings, FiMoreHorizontal, FiServer, FiTrash2, FiCalendar,
} from 'react-icons/fi';
import { IoHome, IoAnalytics, IoScan, IoLocation, IoDocumentText, IoVideocam } from 'react-icons/io5';
import { FiClock } from 'react-icons/fi';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { logout } from '../../utils/logout';
import api from '../../utils/api';
import { isAssessmentStudent } from '../../utils/assessmentStudent';



function Navbar({ onToggleSidebar }) {
  const location   = useLocation();
  const { user }   = useUser();
  const assessmentStudent = isAssessmentStudent(user);

  // ── Standard nav state ──────────────────────────────────────────────────────
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMoreMenu,    setShowMoreMenu]    = useState(false);
  const [showTimesheet,   setShowTimesheet]   = useState(user && user.role_id != '2');
  const [showPM,          setShowPM]          = useState(true);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const profileRef    = useRef();
  const desktopMoreRef = useRef();
  const mobileMoreRef  = useRef();

  const isActive = (path) => location.pathname.startsWith(path);

  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;
  const profileImageUrl = user?.image
    ? (user.image.startsWith('http') ? user.image : `${IMAGE_BASE_URL}/${user.image}`)
    : `${IMAGE_BASE_URL}/uploads/no_image.png`;

  // Session bridge removed — is_loggedin() now accepts JWT Bearer token directly.
  // The axios interceptor in api.js attaches Authorization: Bearer <token> to all requests.

  // ── Entitlement fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    if (user.role_id != '2') { setShowTimesheet(true); setShowPM(true); return; }
    (async () => {
      try {
        const [tsRes, pmRes] = await Promise.all([
          api.get('/timesheet/user-config'),
          api.get('/project-management/check-entitlement'),
        ]);
        if (tsRes.data?.status) setShowTimesheet(!!tsRes.data.requires_approval);
        if (pmRes.data?.status !== undefined) setShowPM(pmRes.data.status);
      } catch {}
    })();
  }, [user]);

  // ── Click-outside handler ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
      if (
        desktopMoreRef.current && !desktopMoreRef.current.contains(e.target) &&
        mobileMoreRef.current  && !mobileMoreRef.current.contains(e.target)
      ) setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);



  const toCamel = (s) =>
    s?.toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // ── Menu definitions ────────────────────────────────────────────────────────
  const menuItems = [
    { id: 'home',      label: 'HOME',            path: '/home',              icon: IoHome,        primary: true,  show: (u) => u?.role_id != 2 && !isAssessmentStudent(u) },
    { id: 'tickets',   label: 'TICKETS',          path: '/tickets',           icon: FiCheckSquare, primary: true  },
    { id: 'timesheet', label: 'TIMESHEET',        path: '/timesheet',         icon: FiClock,       primary: true,  isTimesheet: true },
    { id: 'dashboard', label: 'Reports',          path: '/dashboard',         icon: IoAnalytics,   primary: false },
    { id: 'face',      label: 'Attendance',       path: '/face-attendance',   icon: IoScan,        primary: false, show: (u) => u?.role_id != 1 && u?.role_id != 2 && !isAssessmentStudent(u) },
    { id: 'pm',        label: 'Project Workflow', path: '/project-management',icon: IoDocumentText,primary: false, show: (u) => showPM && (u?.role_id == 2 || !isAssessmentStudent(u)) },
    { id: 'geo',       label: 'Tracking',         path: '/geofencing',        icon: IoLocation,    primary: false, show: (u) => u?.role_id == 1 },
    { id: 'docs',      label: 'Documentation',    path: '/documentation',     icon: IoDocumentText,primary: false, show: (u) => u?.role_id != 2 },
    { id: 'assess',    label: 'LMS',       path: '/assessment',        icon: FiCheckSquare, primary: false, show: (u) => u?.role_id != 2 },
      // { id: 'meetings',  label: 'Meetings',         path: '/meetings',          icon: IoVideocam,    primary: false, show: (u) => u?.role_id != 2 },
    { id: 'settings',  label: 'Settings',         path: '/email-settings',    icon: FiSettings,    primary: false, show: (u) => u?.role_id == 1 },
  ];

  const filtered = menuItems.filter((item) => {
    if (item.isTimesheet && !showTimesheet) return false;
    if (item.show && !item.show(user)) return false;
    return true;
  });

  const primaryMenus = filtered.filter((i) => i.primary);
  const moreMenus    = filtered.filter((i) => !i.primary);
  const activeItem   = filtered.find((i) => location.pathname.startsWith(i.path));
  const isEdcRoute = location.pathname === '/server-dashboard' || location.pathname === '/infrastructure-dashboard';
  const currentLabel = isEdcRoute
    ? 'eDC Server Management'
    : (activeItem?.label || 'Menu');

  // ── eDC (single entry — tabs inside dashboard) ─────────────────────────────
  const edcPath = '/server-dashboard';
  const canAccessEdc = user?.role_id == '1' || user?.role_id == '6';

  // ── Shared link styles ──────────────────────────────────────────────────────
  const navLinkClass = (active) =>
    `flex flex-col items-center justify-center p-2 rounded-lg transition-all relative group min-w-[70px]
     ${active ? 'text-main' : 'text-gray-700 hover:text-main hover:bg-gray-100'}`;

  return (
    <>
      {/* ─────────────────────────── NAVBAR BAR ─────────────────────────── */}
      <header className="relative h-[60px] bg-white shadow-sm flex items-center justify-between px-2 sm:px-4 border-b border-neutral-200 min-w-0 z-30">
        <div className="flex items-center justify-between w-full h-full">

          {/* Branding */}
          <div className="flex items-center">
            {user?.role_id != 2 && !assessmentStudent && (
              <button onClick={onToggleSidebar} className="text-gray-700 hover:text-main mr-2">
                <FiMenu className="text-2xl" />
              </button>
            )}
            <Link to={assessmentStudent || user?.role_id == 2 ? '/assessment' : '/home'} className="flex items-center cursor-pointer">
              <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain" />
              <span className="ml-2 font-heading text-main font-medium hidden sm:block">eNova Administration Portal</span>
              <span className="ml-2 font-heading text-main font-semibold block sm:hidden text-sm">eNova STS</span>
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center h-full sm:gap-2">

            {/* ── Desktop nav ─────────────────────────────────────────────── */}
            {!assessmentStudent && (
              <nav id="navbar-main-menu" className="hidden md:flex items-center h-full gap-2 mr-4">
                {primaryMenus.map((item) => (
                  <Link key={item.id} to={item.path}
                    className={navLinkClass(isActive(item.path))}
                  >
                    <item.icon className="text-xl" />
                    <span className="text-[10px] font-bold mt-0.5 tracking-tight uppercase">{item.label}</span>
                    {isActive(item.path) && <div className="absolute bottom-0 left-1 w-full h-0.5 bg-main rounded-full" />}
                  </Link>
                ))}

                {/* eDC */}
                {canAccessEdc && (
                  <div className="relative h-full flex items-center">
                    <Link
                      to={edcPath}
                      className={navLinkClass(isEdcRoute)}
                    >
                      <FiServer className="text-xl" />
                      <span className="text-[10px] font-bold mt-0.5 tracking-tight uppercase">
                        eDC
                      </span>
                      {isEdcRoute && (
                        <div className="absolute bottom-0 left-1 w-full h-0.5 bg-main rounded-full" />
                      )}
                    </Link>
                  </div>
                )}

                {/* More dropdown */}
                {moreMenus.length > 0 && (
                  <div className="relative h-full flex items-center" ref={desktopMoreRef}
                    onMouseEnter={() => setShowMoreMenu(true)}
                    onMouseLeave={() => setShowMoreMenu(false)}
                  >
                    <button onClick={() => setShowMoreMenu(!showMoreMenu)}
                      className={navLinkClass(showMoreMenu || moreMenus.some((m) => isActive(m.path)))}
                    >
                      <FiMoreHorizontal className="text-xl" />
                      <span className="text-[10px] font-bold mt-0.5 tracking-tight uppercase flex items-center">
                        MORE <FiChevronDown className={`ml-0.5 transition-transform ${showMoreMenu ? 'rotate-180' : ''}`} />
                      </span>
                    </button>
                    {showMoreMenu && (
                      <div className="absolute right-0 translate-x-6 top-full pt-1 z-[310]">
                        <div className="w-52 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden py-1">
                          {moreMenus.map((item) => (
                            <Link key={item.id} to={item.path}
                              onClick={() => setShowMoreMenu(false)}
                              className={`flex items-center px-4 py-3 text-sm transition
                                ${isActive(item.path) ? 'bg-main text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-main'}`}
                            >
                              <item.icon className="text-lg mr-3" />
                              <span className="font-medium">{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </nav>
            )}

            {/* ── Mobile nav pill ─────────────────────────────────────────── */}
            {!assessmentStudent && (
              <div id="navbar-mobile-menu" className="flex md:hidden items-center flex-1 justify-center px-2">
                <div className="flex items-center bg-gray-100 rounded-full px-4 py-1.5 shadow-inner">
                  <span className="text-main font-bold text-xs uppercase truncate max-w-[110px]">{currentLabel}</span>
                  <div className="w-px h-4 bg-gray-300 mx-3" />
                  <div className="relative" ref={mobileMoreRef}>
                    <button onClick={() => setShowMoreMenu(!showMoreMenu)} className="p-1 hover:bg-gray-200 rounded-full">
                      <FiMoreHorizontal className="text-xl text-gray-700" />
                    </button>
                    {showMoreMenu && (
                      <div className="fixed top-14 left-4 right-4 bg-white border rounded-2xl shadow-2xl p-4 grid grid-cols-2 gap-3 z-[310] max-h-[80vh] overflow-y-auto">
                        {filtered.map((item) => (
                          <Link key={item.id} to={item.path}
                            onClick={() => setShowMoreMenu(false)}
                            className={`flex flex-col items-center p-4 rounded-xl ${location.pathname.startsWith(item.path) ? 'bg-main text-white' : 'bg-gray-50 text-gray-700'}`}
                          >
                            <item.icon className="text-2xl mb-1" />
                            <span className="text-xs font-bold text-center">{item.label}</span>
                          </Link>
                        ))}
                        {/* eDC mobile */}
                        {canAccessEdc && (
                          <Link to={edcPath} onClick={() => setShowMoreMenu(false)}
                            className={`flex flex-col items-center p-4 rounded-xl ${isEdcRoute ? 'bg-main text-white' : 'bg-gray-50 text-gray-700'}`}
                          >
                            <FiServer className="text-2xl mb-1" />
                            <span className="text-xs font-bold text-center">eDC</span>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Profile menu ─────────────────────────────────────────────── */}
            <div className="flex items-center" ref={profileRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-800 hover:bg-main hover:text-white transition group"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 bg-gray-200">
                  {user?.image
                    ? <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                    : <FiUser className="text-gray-500 w-full h-full p-1.5 group-hover:text-white" />
                  }
                </div>
                <span className="hidden sm:inline font-medium truncate max-w-[8rem]">
                  {toCamel(user?.first_name)} {toCamel(user?.last_name)}
                </span>
                <FiChevronDown className={`text-base transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
              </button>
              {showProfileMenu && (
                <div className="absolute right-4 mt-12 top-0 bg-white border border-gray-100 rounded-lg shadow-xl z-[310] w-44 flex flex-col text-sm overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <p className="text-xs font-bold truncate">{user?.first_name} {user?.last_name}</p>
                  </div>
                  <Link to="/profile" onClick={() => setShowProfileMenu(false)}
                    className="flex items-center px-4 py-3 hover:bg-gray-100 border-b border-gray-50 text-gray-700">
                    <FiUser className="mr-2" /> Profile
                  </Link>
                  <button onClick={() => { setShowProfileMenu(false); logout(); }}
                    className="flex items-center px-4 py-3 hover:bg-red-50 hover:text-red-600 text-left text-gray-700">
                    <FiLogOut className="mr-2" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ─────────────── eDC FULL-PAGE OVERLAYS ─────────────────────────── */}


      {/* ─────────────── ACTION MODALS ───────────────────────────────────── */}
    </>
  );
}

export default Navbar;